import type { RawMqttMessage } from '@/lib/mqtt/types';
import { cn } from '@/lib/utils';
import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { buildCopyText, writeClipboard } from '../utils/copyMessage';

const FEEDBACK_MS = 1400;

/** Copies the selected message's contents (mockup `.ds-copy-btn`, L5064). */
export function CopyMessageButton({ selected }: { selected: RawMqttMessage | null }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const onCopy = async () => {
    const text = buildCopyText(selected);
    if (!text) return;
    const ok = await writeClipboard(text);
    setState(ok ? 'copied' : 'failed');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), FEEDBACK_MS);
  };

  const label = state === 'copied' ? 'Copied!' : state === 'failed' ? 'Failed' : 'Copy';

  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={!selected}
      aria-label="Copy message contents to clipboard"
      title="Copy message contents to clipboard"
      className={cn(
        'ml-1 flex cursor-pointer items-center gap-1 rounded border px-2 py-[3px] font-sans text-[0.769em] font-bold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        state === 'copied'
          ? 'border-(--color-green) bg-(--color-green-bg) text-(--color-green)'
          : state === 'failed'
            ? 'border-(--color-red) text-(--color-red)'
            : 'border-(--color-border) bg-(--color-bg) text-(--color-text3) hover:border-(--color-green) hover:bg-(--color-green-bg) hover:text-(--color-green)',
      )}
    >
      {state === 'copied' ? <Check size={11} /> : <Copy size={11} />}
      {label}
    </button>
  );
}
